import { Button, Crown, Star } from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";

interface SubscriptionNoticeProps {
  title: string;
  description: string;
  feature: string;
  onSubscribe?: () => void;
  onContact?: () => void;
}

export const SubscriptionNotice: React.FC<SubscriptionNoticeProps> = ({
  title,
  description,
  feature,
  onSubscribe: _onSubscribe,
}) => {
  const { t } = useTranslation();

  return (
    <div className="overflow-hidden relative p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400 rounded-full translate-x-16 -translate-y-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-400 rounded-full -translate-x-12 translate-y-12"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center mb-4 space-x-3">
          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-amber-900">{title}</h2>
            <p className="text-sm text-amber-700">{description}</p>
          </div>
        </div>

        {/* Feature Highlight */}
        <div className="p-4 mb-6 rounded-lg border border-amber-200 backdrop-blur-sm bg-white/60">
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-10 h-10 bg-amber-100 rounded-lg">
              <Star className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-900">{feature}</h3>
              <p className="text-sm text-amber-700">
                {t("emergency.subscription.featureDescription")}
              </p>
            </div>
          </div>
        </div>

        {/* Benefits List */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2">
          <div className="flex items-start space-x-3">
            <div className="flex justify-center items-center w-6 h-6 rounded-full bg-green-100 mt-0.5">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-amber-900">
                {t("emergency.subscription.features.monitoring.title")}
              </h4>
              <p className="text-xs text-amber-700">
                {t("emergency.subscription.features.monitoring.description")}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex justify-center items-center w-6 h-6 rounded-full bg-green-100 mt-0.5">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-amber-900">
                {t("emergency.subscription.features.staff.title")}
              </h4>
              <p className="text-xs text-amber-700">
                {t("emergency.subscription.features.staff.description")}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex justify-center items-center w-6 h-6 rounded-full bg-green-100 mt-0.5">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-amber-900">
                {t("emergency.subscription.features.equipment.title")}
              </h4>
              <p className="text-xs text-amber-700">
                {t("emergency.subscription.features.equipment.description")}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex justify-center items-center w-6 h-6 rounded-full bg-green-100 mt-0.5">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-amber-900">
                {t("emergency.subscription.features.analytics.title")}
              </h4>
              <p className="text-xs text-amber-700">
                {t("emergency.subscription.features.analytics.description")}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
          <div className="relative">
            <Button
              disabled
              className="relative text-white bg-gradient-to-r from-amber-400 to-orange-400 border-0 opacity-60 cursor-not-allowed"
            >
              <Crown className="mr-2 w-4 h-4" />
              {t("emergency.subscription.subscribeNow")}
            </Button>
            <span className="absolute -top-2 -right-2 px-2 py-1 text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shadow-lg animate-pulse">
              {t("emergency.subscription.comingSoonTag")}
            </span>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-4 text-center">
          <p className="text-xs text-amber-600">
            {t("emergency.subscription.premiumNote")}
          </p>
        </div>
      </div>
    </div>
  );
};
